import { Calendar, Clock, Download, Loader2, Users, Video, XCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { downloadSessionNotesPdf } from "@/lib/sessionToolsQueries";
import InlineFileAttachments from "../files/InlineFileAttachments";

/**
 * Master v5 Prompt 10 — one Session row. Always shows the coach's
 * America/Sao_Paulo time as primary and the viewer's own time as
 * secondary.
 *
 * Master v5 Prompt 31.1 — "Client Timezone on File": `preferredTimezone`
 * (the viewer's stored `me.timezone`, when they've set one) now takes
 * priority over the auto-detected browser timezone for that secondary
 * line — a browser can be wrong (VPN, shared device, travel) in a way a
 * deliberately-set preference isn't.
 */
function formatDualTime(iso, coachTimezone, preferredTimezone) {
  const d = new Date(iso);
  const viewerTz = preferredTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const fmt = (tz) => new Intl.DateTimeFormat("en-US", {
    weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: tz,
  }).format(d);
  const primary = `${fmt(coachTimezone)} (São Paulo)`;
  const secondary = viewerTz && viewerTz !== coachTimezone ? `${fmt(viewerTz)} (your time)` : null;
  return { primary, secondary };
}

export { formatDualTime };

const STATE_BADGE = {
  booked: { label: "Booked", cls: "bg-ink-blue/10 text-ink-blue" },
  join_window: { label: "Join window open", cls: "bg-mustard text-ink font-semibold animate-pulse" },
  live: { label: "Live now", cls: "bg-terracotta text-paper" },
  ended: { label: "Completed", cls: "bg-ink/10 text-ink/60" },
  cancelled: { label: "Cancelled", cls: "bg-ink/5 text-ink/40" },
  pending_confirmation: { label: "Pending confirmation", cls: "bg-mustard/30 text-ink font-semibold" },
};

export function SessionCard({ session, isCoach, onJoin, onCancel, onMarkNoShow, onReschedule, onComplete, joining, cancelling, workspaceId, viewerTimezone }) {
  const [downloadingNotes, setDownloadingNotes] = useState(false);
  const { primary, secondary } = formatDualTime(session.starts_at, session.coach_timezone, viewerTimezone);
  const badge = STATE_BADGE[session.status === "no_show" ? "cancelled" : session.room_state] || STATE_BADGE.booked;
  // Master v5 Prompt 31 — a coach may enter their own Room any time
  // before it ends/is cancelled (even days ahead); a client stays
  // gated to the 30-minute join window exactly as before.
  const canJoin = isCoach
    ? (session.status === "booked" || session.status === "pending_confirmation") && session.room_state !== "ended"
    : (session.room_state === "join_window" || session.room_state === "live");
  // Master v5 Prompt 31.1 — "Reschedule Pending Sessions": a
  // pending_confirmation Session (never had a real TidyCal booking or a
  // debited credit yet — see session_repository.cancel_session's own
  // docstring) can now be cancelled/rescheduled too, in any room_state.
  const canCancel = session.status === "pending_confirmation" || (session.status === "booked" && (session.room_state === "booked" || session.room_state === "join_window"));
  const needsCompletion = isCoach && session.status === "booked";
  // "Notes Export" — once a session has actually happened, the coach can
  // grab its Shared Notes as a PDF straight from here, no need to
  // reopen the Room. Downloads gracefully even if no notes were ever
  // taken (the PDF says so plainly).
  const canDownloadNotes = isCoach && (session.status === "completed" || session.room_state === "ended");

  async function handleDownloadNotes() {
    setDownloadingNotes(true);
    try {
      await downloadSessionNotesPdf(workspaceId, session.ses_id);
    } catch (err) {
      toast.error(err.message || "Couldn't download the notes PDF. Please try again.");
    } finally {
      setDownloadingNotes(false);
    }
  }

  return (
    <div className="bg-white/60 rounded-xl border border-ink/10 p-5 flex flex-col gap-3" data-testid={`session-card-${session.ses_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-serif text-ink text-lg" data-testid="session-card-title">{session.title}</p>
          <p className="text-sm text-ink/70 flex items-center gap-1.5 mt-1" data-testid="session-card-time-primary">
            <Calendar size={13} /> {primary}
          </p>
          {secondary && (
            <p className="text-xs text-ink/45 flex items-center gap-1.5 mt-0.5" data-testid="session-card-time-secondary">
              <Clock size={12} /> {secondary}
            </p>
          )}
          {session.visibility_scope === "workspace_shared" && (
            <p className="text-xs text-ink/45 flex items-center gap-1.5 mt-0.5"><Users size={12} /> Shared with the team</p>
          )}
        </div>
        <span className={`text-xs font-medium px-2.5 py-1 rounded-full shrink-0 ${badge.cls}`} data-testid="session-card-state-badge">
          {session.status === "no_show" ? "No-show" : session.status === "completed" ? "Session completed" : badge.label}
        </span>
      </div>

      {session.status === "cancelled" && session.cancelled_reason && (
        <p className="text-xs text-ink/50 italic" data-testid="session-card-cancelled-reason">Reason: {session.cancelled_reason}</p>
      )}
      {session.rescheduled_from_session_id && (
        <p className="text-xs text-ink/50 italic" data-testid="session-card-rescheduled-note">Rescheduled from a previous session</p>
      )}
      {session.status === "pending_confirmation" && (
        <p className="text-xs text-ink/50 italic" data-testid="session-card-pending-note">
          {isCoach
            ? "Action needed: add this exact time to your calendar/TidyCal so it's fully confirmed — check your email for the direct link. You can already enter the Room to check materials/Zoom."
            : "Your coach will confirm this time on the calendar shortly — it will move to \"Booked\" automatically."}
        </p>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {canJoin && (
          <button
            onClick={() => onJoin(session.ses_id)}
            disabled={joining}
            className="flex items-center gap-1.5 bg-terracotta text-paper text-sm font-medium px-4 py-2 rounded-md hover:bg-terracotta/90 disabled:opacity-50"
            data-testid="session-card-join-btn"
          >
            {joining ? <Loader2 size={14} className="animate-spin" /> : <Video size={14} />} {isCoach ? "Enter the session" : "Go to the Rehearsal Room"}
          </button>
        )}
        {!canJoin && session.room_state === "booked" && (
          <p className="text-xs text-ink/45" data-testid="session-card-join-hint">The Rehearsal Room opens 30 minutes before the start time.</p>
        )}
        {canCancel && (
          <button
            onClick={() => onCancel(session.ses_id)}
            disabled={cancelling}
            className="flex items-center gap-1.5 text-terracotta text-xs font-medium hover:underline disabled:opacity-50"
            data-testid="session-card-cancel-btn"
          >
            <XCircle size={13} /> Cancel
          </button>
        )}
        {isCoach && canCancel && onReschedule && (
          <button onClick={() => onReschedule(session)} className="text-xs text-ink/60 hover:text-ink underline" data-testid="session-card-reschedule-btn">
            Reschedule
          </button>
        )}
        {isCoach && session.status === "booked" && session.room_state === "ended" && (
          <button
            onClick={() => onMarkNoShow(session.ses_id)}
            className="text-xs text-ink/50 hover:text-ink underline"
            data-testid="session-card-mark-no-show-btn"
          >
            Mark as no-show
          </button>
        )}
        {needsCompletion && onComplete && (
          <button
            onClick={() => onComplete(session)}
            className="flex items-center gap-1.5 bg-ink text-paper text-xs font-medium px-3 py-1.5 rounded-md hover:bg-ink/85"
            data-testid="session-card-complete-btn"
          >
            Complete Session
          </button>
        )}
        {canDownloadNotes && (
          <button
            onClick={handleDownloadNotes}
            disabled={downloadingNotes}
            className="flex items-center gap-1.5 text-xs text-ink/70 hover:text-ink underline disabled:opacity-50"
            data-testid="session-card-download-notes-btn"
          >
            {downloadingNotes ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Download Notes (PDF)
          </button>
        )}
      </div>

      {session.status !== "cancelled" && workspaceId && (
        <div className="pt-2 border-t border-ink/10">
          <InlineFileAttachments workspaceId={workspaceId} targetType="session" targetId={session.ses_id} label={`the session "${session.title}"`} compact />
        </div>
      )}
    </div>
  );
}
